import { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Button,
  ThemeProvider,
  Tabs,
  Tab,
  useMediaQuery
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import NavBar from '../../layout/NavBar';
import { useAuth } from '../../context/AuthContext';
import { GruppenApi } from '../../../services/api';
import { darkTheme } from '../../../theme/darkTheme';
import { useNavigate, useParams } from 'react-router-dom';
import Notification from '../../util/notifications/Notification';
import BackButton from '../../util/buttons/BackButton';
import NavBarBot from '../../layout/NavBarBot';
import HeaderCard from '../../layout/HeaderCard';
import TabPanel from './components/gruppendetail/TabPanel';
import MitgliederTab from './components/gruppendetail/MitgliederTab';
import AktivitaetTab from './components/gruppendetail/AktivitaetTab';
import StatistikenTab from './components/gruppendetail/StatistikenTab';
import EinladenDialog from './components/gruppendetail/EinladenDialog';

function GruppenDetail() {
  const { nutzer } = useAuth();
  const navigate = useNavigate();
  const { gruppeId } = useParams();
  const isMobile = useMediaQuery(darkTheme.breakpoints.down('md'));
  const [, setShowNotification] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [mitglieder, setMitglieder] = useState([]);
  const [stats, setStats] = useState(null);
  const [, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Einladungs-Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    fetchData();
  }, [gruppeId]);

  const fetchData = async () => {
    try {
      const [mitgliederRes, statsRes] = await Promise.all([
        GruppenApi.getMitglieder(gruppeId),
        GruppenApi.getGruppenStats(gruppeId)
      ]);
      setMitglieder(mitgliederRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('Fehler beim Laden der Daten:', err);
      setMessage({ type: "error", text: "Daten konnten nicht geladen werden." });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setSearching(true);
    try {
      const response = await GruppenApi.searchNutzer(searchQuery, gruppeId);
      setSearchResults(response.data);
    } catch (err) {
      console.error('Fehler bei der Suche:', err);
      setMessage({ type: "error", text: "Suche fehlgeschlagen." });
    } finally {
      setSearching(false);
    }
  };

  const handleInvite = async (empfaengerId) => {
    try {
      console.log(123)
      const response = await GruppenApi.createEinladung({
        gruppe_id: parseInt(gruppeId),
        einlader_id: nutzer.id,
        empfaenger_id: empfaengerId
      });
      console.log("Einladungsantwort:", response.data);
      setMessage({ type: "success", text: "Einladung erfolgreich versendet." });
      setSearchQuery('');
      setSearchResults([]);
      setDialogOpen(false);

    } catch (err) {
      console.error('Fehler beim Einladen:', err);
      setMessage({ type: "error", text: "Einladung konnte nicht versendet werden." });
    }
  };

  const handleRemoveMitglied = async (mitgliedId, mitgliedName) => {
    if (!window.confirm(`Möchtest du ${mitgliedName} wirklich aus der Gruppe entfernen?`)) {
      return;
    }

    try {
      await GruppenApi.removeMitglied({
        gruppeId: parseInt(gruppeId),
        mitgliedId,
        erstellerId: nutzer.id
      });
      setMessage({ type: "success", text: "Mitglied erfolgreich entfernt." });
      fetchData();
    } catch (err) {
      console.error('Fehler beim Entfernen:', err);
      setMessage({ type: "error", text: "Mitglied konnte nicht entfernt werden." });
    }
  };

  const openKalender = () => {
    navigate(`/gruppen/${gruppeId}/kalender`)
  };

  const ersteller = mitglieder.find(m => m.ist_ersteller);
  const isErsteller = ersteller?.id === nutzer.id;

  return (
    <>
      <ThemeProvider theme={darkTheme}>
        <Box sx={{ bgcolor: 'background.default', minHeight: '100vh', pb: 4 }}>
          <NavBar />
          <Container maxWidth="lg" sx={{ pt: { xs: 2, md: 4 } }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
              {!isMobile && (
                <BackButton />
              )}
              <HeaderCard title={"Gruppendetails"} subtitle={`${mitglieder.length} Mitglied${mitglieder.length !== 1 ? 'er' : ''}`} />
            </Box>
            <Box sx={{ mb: 2 }}>
              {!isMobile && (
                <Button
                  variant="outlined"
                  startIcon={<EventIcon />}
                  onClick={() => navigate(`/gruppen/${gruppeId}/kalender`)}
                >
                  Kalender
                </Button>
              )}
            </ Box>
            {message.status === "error" || message.status === "success" && (
              <Notification
                type={message.type}
                message={message.text}
                onClose={() => {
                  setShowNotification(true);
                  setMessage({ type: "", text: "" });
                }}
              />
            )}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="Mitglieder" />
                <Tab label="Aktivität" />
                <Tab label="Statistiken" />
              </Tabs>
            </Box>

            {/* Tab: Mitglieder */}
            <TabPanel value={tabValue} index={0}>
              <MitgliederTab
                mitglieder={mitglieder}
                isErsteller={isErsteller}
                onInviteOpen={() => setDialogOpen(true)}
                onRemove={handleRemoveMitglied}
              />
            </TabPanel>

            {/* Tab: Aktivität */}
            <TabPanel value={tabValue} index={1}>
              <AktivitaetTab stats={stats} />
            </TabPanel>

            {/* Tab: Statistiken */}
            <TabPanel value={tabValue} index={2}>
              <StatistikenTab stats={stats} />
            </TabPanel>
          </Container>

          {/* Dialog: Mitglied einladen */}
          <EinladenDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            searchQuery={searchQuery}
            onSearchQuery={setSearchQuery}
            onSearch={handleSearch}
            searching={searching}
            searchResults={searchResults}
            onInvite={handleInvite}
          />
        </Box>
      </ThemeProvider >
      <NavBarBot mainBtnF={openKalender} mainBtnTxt={<EventIcon />} />
    </>
  );
}

export default GruppenDetail;
